import { useCallback, useEffect, useMemo, useState } from "react";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { listTranscripts } from "../../../shared/api/transcriptsApi";
import {
  TranscriptListItem,
  TranscriptListResponse,
} from "../../../shared/interfaces/Transcript";
import { useToast } from "../../../shared/toast";
import {
  transcriptsCjiCacheOptions,
  transcriptsErrorMessage,
  transcriptsKeys,
} from "./transcriptsQueries";

const PAGE_SIZE = 50;

/**
 * Server-paginated transcript list on react-query (the useListVideos
 * pattern). The external API is identical to the old hand-rolled version:
 * - a search change "replaces": it re-keys the query; the previous rows stay
 *   on screen via placeholderData while the new page 1 loads, exactly like
 *   the old hook, which only overwrote `transcripts` once the response landed,
 * - `loadMore` "appends" through the infinite-query cursor,
 * - `refresh` restarts from page 1.
 * react-query's per-key AbortSignal replaces the old manual AbortController:
 * a superseded or unmounted fetch is cancelled and never commits.
 */
export const useListTranscripts = (search = "") => {
  const toast = useToast();
  const queryClient = useQueryClient();

  // Trimmed once so it keys the query stably; whitespace-only searches are
  // treated as "no search" (see SEARCH_BACKEND_SPEC.md).
  const trimmedSearch = search.trim();

  const query = useInfiniteQuery({
    queryKey: transcriptsKeys.list(trimmedSearch),
    queryFn: ({ pageParam, signal }) =>
      listTranscripts(
        {
          limit: PAGE_SIZE,
          nextToken: pageParam,
          search: trimmedSearch || undefined,
        },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    // `|| undefined` keeps an empty-string token meaning "no more pages",
    // matching the old `setHasMore(!!data.nextToken)`.
    getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
    placeholderData: keepPreviousData,
    ...transcriptsCjiCacheOptions,
  });

  const transcripts = useMemo<TranscriptListItem[]>(
    () => query.data?.pages.flatMap((page) => page.transcripts ?? []) ?? [],
    [query.data],
  );

  // Latches false after the first settle (success or error) and never
  // re-trues — the old hook flipped its flag once, in the first fetch's
  // `finally`, and left it false across later search changes. Same
  // effect-based latch as useListVideos.
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (isInitialLoad && (query.isSuccess || query.isError)) {
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, query.isSuccess, query.isError]);

  // Surface fetch failures exactly as before: same copy, via toast, while the
  // last-good rows stay on screen (the error never replaces `transcripts`).
  // Keyed on errorUpdatedAt, not error identity: every failed fetch bumps the
  // timestamp exactly once, replaying the old one-toast-per-failure behavior.
  const { error, errorUpdatedAt } = query;
  useEffect(() => {
    if (errorUpdatedAt > 0 && error) {
      toast.error(
        transcriptsErrorMessage(error, "Failed to load transcripts"),
      );
    }
  }, [error, errorUpdatedAt, toast]);

  const { hasNextPage, isFetching, fetchNextPage, refetch } = query;

  const loadMore = useCallback(() => {
    // Same guards as before: a cursor to follow and no fetch in flight.
    if (hasNextPage && !isFetching) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  const refresh = useCallback(() => {
    // The old "replace" refresh restarted from page 1. A bare refetch() on an
    // infinite query replays EVERY cached page, so trim to page 1 first (the
    // useListVideos trim-then-refetch pattern) and refetch exactly one page.
    queryClient.setQueryData<
      InfiniteData<TranscriptListResponse, string | undefined>
    >(transcriptsKeys.list(trimmedSearch), (data) =>
      data && data.pages.length > 1
        ? {
            pages: data.pages.slice(0, 1),
            pageParams: data.pageParams.slice(0, 1),
          }
        : data,
    );
    void refetch();
  }, [queryClient, trimmedSearch, refetch]);

  return {
    transcripts,
    // isFetching, not isPending: the old flag was true during EVERY fetch —
    // initial, search replace, and append.
    isLoading: isFetching,
    isInitialLoad,
    hasMore: hasNextPage,
    loadMore,
    refresh,
  };
};
